import React from 'react';
import { useUnreadMessagesCount } from '@cp/entities/dialog';
import { useClientWebRouteParams, usePathGenerator } from '@cp/entities/clientWebRoutes';
import { coreCurrentClientApi } from '@cp/entities/client';
import { useIsLoggedIn } from '@cp/entities/currentUser';
import { useGetQueryParams } from '@cp/shared/queryParams';
import { ClientType } from '@common/model/client/clientType';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { useNavigateToCatalog } from 'src/features/catalog';
import { useBurgerMenuController } from 'src/features/burgerMenu';
import { useWithTrack } from 'src/shared/tracking';
import { useAuth, useAuthWithQuiz } from 'src/features/auth';
import { useExperimentMatch } from 'src/shared/ab';
import { findHelperEvent, findJobEvent, messagesTabEvent } from '../lib';
import { ItemsCreatorProps, UseItemsProps, UseItemsReturn } from './types';
import { createClientItems } from './createClientItems';
import { createGuestItems } from './createGuestItems';

export const useItems = (props: UseItemsProps): UseItemsReturn => {
  const isNavigationTriggerAuth = useExperimentMatch('bottomMenuAct', 'auth');
  const withTrack = useWithTrack();
  const generateAppPath = usePathGenerator();
  const isLoggedIn = useIsLoggedIn();
  const auth = useAuth();
  const { data: clientProfile } = coreCurrentClientApi.useGetProfileQuery(undefined, {
    skip: !isLoggedIn,
  });
  const routeParams = useClientWebRouteParams();
  const unreadMessagesCount = useUnreadMessagesCount();
  const hasFavoritesHelper = routeParams.pageId === ClientWebPage.Favorites && !clientProfile;
  const hasFindHelper = clientProfile?.type === ClientType.Employer || hasFavoritesHelper;
  const { isLoading: isLoadingNavigate, navigateToCatalog } = useNavigateToCatalog({
    // catalogType: hasFindHelper ? CatalogItemType.Resume : CatalogItemType.Vacancy,
  });
  const onEmployerPreFilterRegistration = useAuthWithQuiz(['cityId']);
  const navigateToCatalogWithAuth = useAuthWithQuiz(['careerId']);
  const queryParams = useGetQueryParams();
  const burgerMenuController = useBurgerMenuController();

  return React.useMemo(() => {
    const creatorProps: ItemsCreatorProps = {
      ...props,
      generateAppPath,
      routeParams,
      unreadMessagesCount,
      isLoadingNavigate,
      navigateToCatalog: withTrack(
        () => {
          return hasFindHelper ? findHelperEvent() : findJobEvent();
        },
        () => {
          if (isLoggedIn) {
            return navigateToCatalog();
          }
          return isNavigationTriggerAuth ? navigateToCatalogWithAuth() : navigateToCatalog();
        },
      ),
      // eslint-disable-next-line @typescript-eslint/no-empty-function,no-empty-function
      onMessagesTabClick: withTrack(messagesTabEvent, () => {}),
      burgerMenuController,
      onLogin: () => auth(),
      onSpecialistRegistration: () => {
        auth();
      },
      onEmployerPreFilterRegistration,
      queryParams,
    };

    if (clientProfile) {
      return createClientItems({
        ...creatorProps,
        clientProfile,
      });
    } else {
      return createGuestItems(creatorProps);
    }
  }, [
    props,
    generateAppPath,
    routeParams,
    unreadMessagesCount,
    isLoadingNavigate,
    withTrack,
    burgerMenuController,
    onEmployerPreFilterRegistration,
    queryParams,
    clientProfile,
    hasFindHelper,
    navigateToCatalog,
    auth,
  ]);
};
